import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Archive as PackageOpen,
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  CalendarClock as CalendarDays,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleDot as MapPin,
  CircleDollarSign,
  Eye as Camera,
  Clock3,
  ExternalLink,
  FileText,
  ListChecks as ClipboardCheck,
  PencilLine as Edit3,
  Phone,
  PhoneForwarded as Navigation,
  Plus,
  Search,
  Trash2,
  Upload as ImagePlus,
  UserRound,
  Users,
  Workflow as Route,
  X,
} from 'lucide-react';
import { api, uploadFormData } from '../../api';
import './field-visits.css';

type VisitStatus = 'PLANNED' | 'EN_ROUTE' | 'ON_SITE' | 'COMPLETED' | 'CANCELLED';

type VisitAssignee = { id: string; name: string; isLead?: boolean };
type VisitMaterial = { name: string; quantity: number; unit: string; cost: number };
type VisitChecklistItem = { id: string; title: string; is_completed: boolean; completed_by_name?: string | null };
type VisitPhoto = { id: string; original_name: string; caption?: string | null; created_at?: string; uploaded_by_name?: string | null };
type VisitEvent = { id: string; description: string; created_at: string; actor_name?: string | null };

type Visit = {
  id: string;
  company_id: string;
  company_name: string;
  contact_id?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  ticket_id?: string | null;
  ticket_display_number?: string | null;
  ticket_subject?: string | null;
  title: string;
  address: string;
  scheduled_start: string;
  scheduled_end?: string | null;
  status: VisitStatus;
  travel_started_at?: string | null;
  onsite_started_at?: string | null;
  completed_at?: string | null;
  travel_minutes: number;
  work_minutes: number;
  expenses: number;
  materials: VisitMaterial[];
  result_note?: string | null;
  confirmation_name?: string | null;
  is_billable: boolean;
  worklog_id?: string | null;
  assignee_ids: string[];
  assignees: VisitAssignee[];
  checklist_total: number;
  checklist_completed: number;
  photos_count: number;
  can_edit: boolean;
  can_delete: boolean;
  can_update_status: boolean;
};

type VisitDetail = Visit & {
  checklist: VisitChecklistItem[];
  photos: VisitPhoto[];
  events: VisitEvent[];
};

type VisitStats = { today: number; active: number; planned: number; attention: number; completed_month: number };
type CompanyOption = { id: string; name: string; address?: string | null; is_active?: boolean };
type ContactOption = { id: string; company_id: string; full_name?: string | null; phone?: string | null; email?: string | null };
type AssigneeOption = { id: string; full_name?: string; fullName?: string; role?: string; roles?: string[] };
type TicketOption = { id: string; company_id: string; number: number; subject: string; status: string };

type EditorChecklist = { id?: string; title: string; isCompleted: boolean };
type EditorMaterial = { name: string; quantity: number; unit: string; cost: number };
type VisitDraft = {
  companyId: string;
  contactId: string;
  ticketId: string;
  title: string;
  address: string;
  scheduledStart: string;
  scheduledEnd: string;
  assigneeUserIds: string[];
  leadUserId: string;
  checklist: EditorChecklist[];
  materials: EditorMaterial[];
  expenses: number;
  isBillable: boolean;
};

type CompletionState = {
  travelMinutes: number;
  workMinutes: number;
  resultNote: string;
  confirmationName: string;
  createWorklog: boolean;
};

const STATUS_META: Record<VisitStatus, { label: string; short: string }> = {
  PLANNED: { label: 'Запланирован', short: 'План' },
  EN_ROUTE: { label: 'В пути', short: 'В пути' },
  ON_SITE: { label: 'На объекте', short: 'На объекте' },
  COMPLETED: { label: 'Завершён', short: 'Готово' },
  CANCELLED: { label: 'Отменён', short: 'Отмена' },
};

const STATUS_FILTERS: Array<{ value: '' | VisitStatus; label: string }> = [
  { value: '', label: 'Все' },
  { value: 'PLANNED', label: 'Запланированы' },
  { value: 'EN_ROUTE', label: 'В пути' },
  { value: 'ON_SITE', label: 'На объекте' },
  { value: 'COMPLETED', label: 'Завершены' },
];

const ROUTE_STEPS: VisitStatus[] = ['PLANNED', 'EN_ROUTE', 'ON_SITE', 'COMPLETED'];

function dateTime(value?: string | null) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

function time(value?: string | null) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

function formatMoney(value: number) {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value || 0) + ' ₽';
}

function initials(value: string) {
  return value.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase() || '—';
}

function toLocalInput(value?: string | null) {
  const date = value ? new Date(value) : new Date(Date.now() + 60 * 60_000);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function emptyDraft(): VisitDraft {
  const start = new Date(Date.now() + 60 * 60_000);
  start.setMinutes(0, 0, 0);
  const end = new Date(start.getTime() + 2 * 60 * 60_000);
  return {
    companyId: '', contactId: '', ticketId: '', title: '', address: '',
    scheduledStart: toLocalInput(start.toISOString()), scheduledEnd: toLocalInput(end.toISOString()),
    assigneeUserIds: [], leadUserId: '', checklist: [], materials: [], expenses: 0, isBillable: true,
  };
}

function visitDraft(visit: VisitDetail): VisitDraft {
  return {
    companyId: visit.company_id,
    contactId: visit.contact_id || '',
    ticketId: visit.ticket_id || '',
    title: visit.title,
    address: visit.address,
    scheduledStart: toLocalInput(visit.scheduled_start),
    scheduledEnd: visit.scheduled_end ? toLocalInput(visit.scheduled_end) : '',
    assigneeUserIds: visit.assignee_ids,
    leadUserId: visit.assignees.find(item => item.isLead)?.id || visit.assignee_ids[0] || '',
    checklist: visit.checklist.map(item => ({ id: item.id, title: item.title, isCompleted: item.is_completed })),
    materials: visit.materials.map(item => ({ ...item })),
    expenses: visit.expenses,
    isBillable: visit.is_billable,
  };
}

function queryString(status: string, search: string) {
  const params = new URLSearchParams({ limit: '300' });
  if (status) params.set('status', status);
  if (search.trim()) params.set('search', search.trim());
  return params.toString();
}

function AvatarStack({ assignees }: { assignees: VisitAssignee[] }) {
  if (!assignees.length) return <span className="visit-unassigned"><UserRound /> Не назначено</span>;
  return <div className="visit-avatar-stack" aria-label={assignees.map(item => item.name).join(', ')}>
    {assignees.slice(0, 3).map(item => <span key={item.id} title={item.name}>{initials(item.name)}</span>)}
    {assignees.length > 3 && <em>+{assignees.length - 3}</em>}
  </div>;
}

function MetricCard({ icon, value, label, note, tone }: { icon: React.ReactNode; value: number; label: string; note: string; tone: string }) {
  return <article className={`visit-metric visit-metric--${tone}`}>
    <span className="visit-metric__icon">{icon}</span>
    <div><strong>{value}</strong><b>{label}</b><small>{note}</small></div>
  </article>;
}

export function FieldVisitsPage({ canPlan, canDelete }: { canPlan: boolean; canDelete: boolean }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'' | VisitStatus>('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [editor, setEditor] = useState<'create' | 'edit' | null>(null);
  const [completionOpen, setCompletionOpen] = useState(false);
  const [completion, setCompletion] = useState({ travelMinutes: 0, workMinutes: 60, resultNote: '', confirmationName: '', createWorklog: true });
  const [error, setError] = useState('');

  const listQuery = useQuery({
    queryKey: ['field-visits', status, search],
    queryFn: () => api<Visit[]>(`/visits?${queryString(status, search)}`),
    refetchInterval: 30_000,
  });
  const statsQuery = useQuery({ queryKey: ['field-visits', 'stats'], queryFn: () => api<VisitStats>('/visits/stats'), refetchInterval: 30_000 });
  const detailQuery = useQuery({
    queryKey: ['field-visits', 'detail', selectedId],
    queryFn: () => api<VisitDetail>(`/visits/${selectedId}`),
    enabled: Boolean(selectedId),
  });
  const visits = listQuery.data || [];
  const detail = detailQuery.data;
  const stats = statsQuery.data || { today: 0, active: 0, planned: 0, attention: 0, completed_month: 0 };

  useEffect(() => {
    if (!visits.length) setSelectedId('');
    else if (!selectedId || !visits.some(item => item.id === selectedId)) setSelectedId(visits[0].id);
  }, [visits, selectedId]);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['field-visits'] }),
      selectedId ? queryClient.invalidateQueries({ queryKey: ['field-visits', 'detail', selectedId] }) : Promise.resolve(),
    ]);
  };

  const statusMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api<VisitDetail>(`/visits/${selectedId}/status`, { method: 'PATCH', body: JSON.stringify(payload) }),
    onSuccess: async () => { setError(''); setCompletionOpen(false); await refresh(); },
    onError: (cause: Error) => setError(cause.message),
  });
  const checklistMutation = useMutation({
    mutationFn: ({ id, checked }: { id: string; checked: boolean }) => api<VisitDetail>(`/visits/${selectedId}/checklist/${id}`, { method: 'PATCH', body: JSON.stringify({ isCompleted: checked }) }),
    onSuccess: refresh,
    onError: (cause: Error) => setError(cause.message),
  });
  const deleteMutation = useMutation({
    mutationFn: () => api(`/visits/${selectedId}`, { method: 'DELETE' }),
    onSuccess: async () => { setSelectedId(''); await refresh(); },
    onError: (cause: Error) => setError(cause.message),
  });
  const photoMutation = useMutation({
    mutationFn: (files: FileList) => {
      const form = new FormData();
      [...files].forEach(file => form.append('photos', file));
      return uploadFormData(`/visits/${selectedId}/photos`, form);
    },
    onSuccess: refresh,
    onError: (cause: Error) => setError(cause.message),
  });

  const beginComplete = () => {
    if (!detail) return;
    setCompletion({
      travelMinutes: detail.travel_minutes || 0,
      workMinutes: detail.work_minutes || 60,
      resultNote: detail.result_note || '',
      confirmationName: detail.confirmation_name || '',
      createWorklog: Boolean(detail.ticket_id),
    });
    setCompletionOpen(true);
  };

  return <main className="field-visits-workspace">
    <header className="field-visits-heading">
      <div>
        <span className="field-visits-eyebrow"><Route /> Полевой сервис</span>
        <h1>Выезды</h1>
        <p>Планирование, команда и результат работ на площадке клиента</p>
      </div>
      {canPlan && <button className="visit-primary-button" type="button" onClick={() => setEditor('create')}><Plus /> Новый выезд</button>}
    </header>

    <section className="visit-metrics" aria-label="Сводка по выездам">
      <MetricCard icon={<CalendarDays />} value={stats.today} label="Сегодня" note="в текущем расписании" tone="blue" />
      <MetricCard icon={<Navigation />} value={stats.active} label="В пути и на объекте" note="работа идёт сейчас" tone="teal" />
      <MetricCard icon={<ClipboardCheck />} value={stats.planned} label="Запланировано" note="предстоящих выездов" tone="violet" />
      <MetricCard icon={<AlertTriangle />} value={stats.attention} label="Требует внимания" note="вышли за временное окно" tone="amber" />
    </section>

    <section className="visit-toolbar">
      <div className="visit-status-tabs" role="tablist" aria-label="Статус выезда">
        {STATUS_FILTERS.map(item => <button key={item.value || 'all'} type="button" className={status === item.value ? 'active' : ''} onClick={() => setStatus(item.value)}>{item.label}</button>)}
      </div>
      <label className="visit-search"><Search /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Компания, адрес или заявка" /></label>
    </section>

    {error && <div className="visit-error" role="alert"><AlertTriangle /><span>{error}</span><button type="button" onClick={() => setError('')} aria-label="Скрыть"><X /></button></div>}

    <div className="visit-layout">
      <section className="visit-list-panel" aria-label="Список выездов">
        <div className="visit-list-panel__head">
          <div><b>Расписание</b><span>{visits.length} {visits.length === 1 ? 'выезд' : 'выездов'}</span></div>
          <small>обновляется автоматически</small>
        </div>
        <div className="visit-list">
          {listQuery.isLoading && <div className="visit-empty"><Clock3 /><b>Загружаем расписание</b></div>}
          {!listQuery.isLoading && !visits.length && <div className="visit-empty"><MapPin /><b>Выездов не найдено</b><span>Измените фильтр или запланируйте новый</span></div>}
          {visits.map(item => <button type="button" key={item.id} onClick={() => setSelectedId(item.id)} className={`visit-list-card ${selectedId === item.id ? 'active' : ''}`}>
            <span className={`visit-list-card__rail visit-tone--${item.status.toLowerCase()}`} />
            <span className="visit-list-card__time"><b>{time(item.scheduled_start)}</b><small>{new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short' }).format(new Date(item.scheduled_start))}</small></span>
            <span className="visit-list-card__main">
              <span className="visit-list-card__top"><em className={`visit-status visit-status--${item.status.toLowerCase()}`}>{STATUS_META[item.status].label}</em>{item.ticket_display_number && <small>{item.ticket_display_number}</small>}</span>
              <strong>{item.title}</strong>
              <span><Building2 /> {item.company_name}</span>
              <span><MapPin /> {item.address}</span>
              <span className="visit-list-card__footer"><AvatarStack assignees={item.assignees} /><small>{item.checklist_completed}/{item.checklist_total} пунктов</small></span>
            </span>
            <ChevronRight className="visit-list-card__arrow" />
          </button>)}
        </div>
      </section>

      <section className="visit-detail-panel" aria-label="Карточка выезда">
        {!selectedId && <div className="visit-detail-empty"><span><Route /></span><h2>Выберите выезд</h2><p>Здесь появятся маршрут выполнения, команда и результат.</p></div>}
        {selectedId && detailQuery.isLoading && <div className="visit-detail-empty"><span><Clock3 /></span><h2>Открываем карточку</h2></div>}
        {detail && <>
          <header className="visit-detail-head">
            <div className="visit-detail-head__title">
              <span className={`visit-detail-icon visit-tone--${detail.status.toLowerCase()}`}><BriefcaseBusiness /></span>
              <div><em className={`visit-status visit-status--${detail.status.toLowerCase()}`}>{STATUS_META[detail.status].label}</em><h2>{detail.title}</h2><p>{detail.company_name}{detail.ticket_display_number ? ` · ${detail.ticket_display_number}` : ''}</p></div>
            </div>
            <div className="visit-detail-actions">
              {detail.can_edit && <button type="button" onClick={() => setEditor('edit')} title="Редактировать"><Edit3 /></button>}
              {canDelete && detail.can_delete && <button type="button" className="danger" onClick={() => { if (window.confirm('Удалить выезд без возможности восстановления?')) deleteMutation.mutate(); }} title="Удалить"><Trash2 /></button>}
            </div>
          </header>

          <section className="visit-route-card">
            <div className="visit-section-title"><div><Route /><span><b>Маршрут выполнения</b><small>Фактический ход работ</small></span></div></div>
            <div className={`visit-route visit-route--${detail.status.toLowerCase()}`}>
              {ROUTE_STEPS.map((step, index) => {
                const currentIndex = ROUTE_STEPS.indexOf(detail.status);
                const reached = detail.status === 'COMPLETED' || (currentIndex >= index && detail.status !== 'CANCELLED');
                const active = detail.status === step;
                return <div key={step} className={`${reached ? 'reached' : ''} ${active ? 'current' : ''}`}>
                  <span>{reached && !active ? <Check /> : index + 1}</span><b>{STATUS_META[step].short}</b>
                </div>;
              })}
            </div>
            {detail.status === 'CANCELLED' && <div className="visit-cancelled-note"><X /> Выезд отменён</div>}
          </section>

          <div className="visit-detail-grid">
            <section className="visit-info-card visit-info-card--wide">
              <div className="visit-section-title"><div><MapPin /><span><b>Место и время</b><small>Окно обслуживания</small></span></div></div>
              <div className="visit-address"><strong>{detail.address}</strong><a href={`https://yandex.ru/maps/?text=${encodeURIComponent(detail.address)}`} target="_blank" rel="noreferrer">Открыть карту <ExternalLink /></a></div>
              <div className="visit-inline-facts">
                <span><CalendarDays /><small>Начало</small><b>{dateTime(detail.scheduled_start)}</b></span>
                <span><Clock3 /><small>Окончание</small><b>{dateTime(detail.scheduled_end)}</b></span>
                <span><Navigation /><small>В пути</small><b>{detail.travel_minutes} мин</b></span>
                <span><BriefcaseBusiness /><small>На объекте</small><b>{detail.work_minutes} мин</b></span>
              </div>
            </section>

            <section className="visit-info-card">
              <div className="visit-section-title"><div><Users /><span><b>Команда</b><small>{detail.assignees.length || 'Нет'} исполнителей</small></span></div></div>
              <div className="visit-team-list">
                {detail.assignees.map(person => <div key={person.id}><span>{initials(person.name)}</span><p><b>{person.name}</b><small>{person.isLead ? 'Ведущий специалист' : 'Исполнитель'}</small></p>{person.isLead && <em>Ведущий</em>}</div>)}
                {!detail.assignees.length && <p className="visit-muted">Команда ещё не назначена</p>}
              </div>
            </section>

            <section className="visit-info-card">
              <div className="visit-section-title"><div><Building2 /><span><b>Клиент</b><small>Контекст выезда</small></span></div></div>
              <div className="visit-client-lines"><strong>{detail.company_name}</strong>{detail.contact_name && <span><UserRound /> {detail.contact_name}</span>}{detail.contact_phone && <a href={`tel:${detail.contact_phone}`}><Phone /> {detail.contact_phone}</a>}{detail.ticket_subject && <span><FileText /> {detail.ticket_display_number} · {detail.ticket_subject}</span>}</div>
            </section>

            <section className="visit-info-card visit-info-card--wide">
              <div className="visit-section-title"><div><ClipboardCheck /><span><b>Чек-лист</b><small>{detail.checklist_completed} из {detail.checklist_total} выполнено</small></span></div><em>{detail.checklist_total ? Math.round(detail.checklist_completed * 100 / detail.checklist_total) : 0}%</em></div>
              <div className="visit-progress"><span style={{ width: `${detail.checklist_total ? detail.checklist_completed * 100 / detail.checklist_total : 0}%` }} /></div>
              <div className="visit-checklist">
                {detail.checklist.map(item => <label key={item.id} className={item.is_completed ? 'checked' : ''}><input type="checkbox" checked={item.is_completed} disabled={!detail.can_update_status || checklistMutation.isPending || ['COMPLETED','CANCELLED'].includes(detail.status)} onChange={event => checklistMutation.mutate({ id: item.id, checked: event.target.checked })} /><span><Check /></span><b>{item.title}</b></label>)}
                {!detail.checklist.length && <p className="visit-muted">Для этого выезда чек-лист не задан</p>}
              </div>
            </section>

            <section className="visit-info-card">
              <div className="visit-section-title"><div><PackageOpen /><span><b>Материалы</b><small>Использовано на объекте</small></span></div></div>
              <div className="visit-materials">
                {detail.materials.map((item, index) => <div key={`${item.name}-${index}`}><span><PackageOpen /></span><p><b>{item.name}</b><small>{item.quantity} {item.unit}</small></p><strong>{formatMoney(item.cost)}</strong></div>)}
                {!detail.materials.length && <p className="visit-muted">Материалы не указаны</p>}
              </div>
              <div className="visit-expenses"><span><CircleDollarSign /> Дополнительные расходы</span><b>{formatMoney(detail.expenses)}</b></div>
            </section>

            <section className="visit-info-card visit-photo-card">
              <div className="visit-section-title"><div><Camera /><span><b>Фотографии</b><small>{detail.photos.length} файлов</small></span></div></div>
              <div className="visit-photo-grid">
                {detail.photos.slice(0, 4).map(photo => <a key={photo.id} href={`/api/v1/visits/${detail.id}/photos/${photo.id}`} target="_blank" rel="noreferrer"><img src={`/api/v1/visits/${detail.id}/photos/${photo.id}`} alt={photo.caption || photo.original_name} /><span>{photo.caption || photo.original_name}</span></a>)}
                <label className="visit-photo-add"><input type="file" accept="image/*" multiple disabled={!detail.can_update_status || photoMutation.isPending} onChange={(event: ChangeEvent<HTMLInputElement>) => { if (event.target.files?.length) photoMutation.mutate(event.target.files); event.target.value = ''; }} /><ImagePlus /><b>Добавить фотографии</b><small>JPG, PNG, WebP до 15 МБ</small></label>
              </div>
            </section>

            {(detail.result_note || detail.status === 'COMPLETED') && <section className="visit-info-card visit-info-card--wide visit-result-card">
              <div className="visit-section-title"><div><CheckCircle2 /><span><b>Результат</b><small>Итог работ на площадке</small></span></div>{detail.worklog_id && <em>Трудозатрата создана</em>}</div>
              <p>{detail.result_note || 'Результат пока не заполнен'}</p>
              {detail.confirmation_name && <span>Подтвердил: <b>{detail.confirmation_name}</b></span>}
            </section>}

            <section className="visit-info-card visit-info-card--wide visit-history-card">
              <div className="visit-section-title"><div><Clock3 /><span><b>История выезда</b><small>Все изменения в хронологическом порядке</small></span></div><em>{detail.events.length}</em></div>
              <div className="visit-history">
                {detail.events.map(event => <article key={event.id}>
                  <span aria-hidden="true" />
                  <div><b>{event.description}</b><small>{event.actor_name || 'Система'} · {dateTime(event.created_at)}</small></div>
                </article>)}
                {!detail.events.length && <p className="visit-muted">История появится после первого действия с выездом</p>}
              </div>
            </section>
          </div>

          {detail.can_update_status && !['COMPLETED', 'CANCELLED'].includes(detail.status) && <footer className="visit-command-bar">
            <div><span className={`visit-status-dot visit-status-dot--${detail.status.toLowerCase()}`} /><p><b>{STATUS_META[detail.status].label}</b><small>{detail.status === 'PLANNED' ? 'Команда готова к выезду' : detail.status === 'EN_ROUTE' ? 'Специалисты направляются к клиенту' : 'Работы выполняются на площадке'}</small></p></div>
            <span>
              <button type="button" className="visit-secondary-button danger" onClick={() => statusMutation.mutate({ status: 'CANCELLED' })}>Отменить</button>
              {detail.status === 'PLANNED' && <button type="button" className="visit-primary-button" onClick={() => statusMutation.mutate({ status: 'EN_ROUTE' })}><Navigation /> Выехать</button>}
              {detail.status === 'EN_ROUTE' && <button type="button" className="visit-primary-button" onClick={() => statusMutation.mutate({ status: 'ON_SITE' })}><MapPin /> На объекте</button>}
              {detail.status === 'ON_SITE' && <button type="button" className="visit-primary-button success" onClick={beginComplete}><CheckCircle2 /> Завершить</button>}
            </span>
          </footer>}
        </>}
      </section>
    </div>

    {editor && <VisitEditor mode={editor} visit={editor === 'edit' ? detail : undefined} onClose={() => setEditor(null)} onSaved={async id => { setEditor(null); if (id) setSelectedId(id); await refresh(); }} />}
    {completionOpen && detail && <CompletionDialog detail={detail} value={completion} onChange={setCompletion} pending={statusMutation.isPending} onClose={() => setCompletionOpen(false)} onSubmit={() => statusMutation.mutate({ status: 'COMPLETED', ...completion })} />}
  </main>;
}

function VisitEditor({ mode, visit, onClose, onSaved }: { mode: 'create' | 'edit'; visit?: VisitDetail; onClose: () => void; onSaved: (id?: string) => void }) {
  const [draft, setDraft] = useState<VisitDraft>(() => visit ? visitDraft(visit) : emptyDraft());
  const [error, setError] = useState('');
  const companiesQuery = useQuery({ queryKey: ['companies'], queryFn: () => api<CompanyOption[]>('/companies') });
  const assigneesQuery = useQuery({ queryKey: ['assignees'], queryFn: () => api<AssigneeOption[]>('/users/assignees') });
  const ticketsQuery = useQuery({ queryKey: ['tickets', 'visit-options'], queryFn: () => api<TicketOption[]>('/tickets') });
  const contactsQuery = useQuery({
    queryKey: ['contacts', 'visit-options', draft.companyId],
    queryFn: () => api<ContactOption[]>(`/contacts?companyId=${draft.companyId}&active=true`),
    enabled: Boolean(draft.companyId),
  });
  const companies = companiesQuery.data || [];
  const assignees = assigneesQuery.data || [];
  const contacts = contactsQuery.data || [];
  const tickets = (ticketsQuery.data || []).filter(item => !draft.companyId || item.company_id === draft.companyId);

  const mutation = useMutation({
    mutationFn: () => api<VisitDetail>(mode === 'edit' && visit ? `/visits/${visit.id}` : '/visits', {
      method: mode === 'edit' ? 'PATCH' : 'POST',
      body: JSON.stringify({
        ...draft,
        contactId: draft.contactId || null,
        ticketId: draft.ticketId || null,
        scheduledStart: new Date(draft.scheduledStart).toISOString(),
        scheduledEnd: draft.scheduledEnd ? new Date(draft.scheduledEnd).toISOString() : null,
        leadUserId: draft.leadUserId || null,
      }),
    }),
    onSuccess: saved => onSaved(saved.id),
    onError: (cause: Error) => setError(cause.message),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!draft.companyId || !draft.title.trim() || !draft.address.trim() || !draft.scheduledStart) {
      setError('Заполните компанию, название, адрес и начало выезда');
      return;
    }
    mutation.mutate();
  };

  const selectCompany = (companyId: string) => {
    const company = companies.find(item => item.id === companyId);
    setDraft(current => ({ ...current, companyId, contactId: '', ticketId: '', address: current.address || company?.address || '' }));
  };

  return <div className="visit-drawer-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <aside className="visit-editor" role="dialog" aria-modal="true" aria-label={mode === 'create' ? 'Новый выезд' : 'Редактирование выезда'}>
      <header><div><span><Route /></span><p><small>Полевой сервис</small><b>{mode === 'create' ? 'Новый выезд' : 'Редактирование выезда'}</b></p></div><button type="button" onClick={onClose} aria-label="Закрыть"><X /></button></header>
      <form onSubmit={submit}>
        {error && <div className="visit-form-error"><AlertTriangle /> {error}</div>}
        <section>
          <h3><span>1</span> Клиент и задача</h3>
          <div className="visit-form-grid">
            <label className="wide"><span>Компания *</span><select value={draft.companyId} onChange={event => selectCompany(event.target.value)}><option value="">Выберите компанию</option>{companies.filter(item => item.is_active !== false).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
            <label><span>Контакт</span><select value={draft.contactId} onChange={event => setDraft(current => ({ ...current, contactId: event.target.value }))}><option value="">Не выбран</option>{contacts.map(item => <option key={item.id} value={item.id}>{item.full_name || item.phone || item.email}</option>)}</select></label>
            <label><span>Заявка</span><select value={draft.ticketId} onChange={event => setDraft(current => ({ ...current, ticketId: event.target.value }))}><option value="">Без заявки</option>{tickets.map(item => <option key={item.id} value={item.id}>ИГ-{item.number} · {item.subject}</option>)}</select></label>
            <label className="wide"><span>Задача выезда *</span><input value={draft.title} onChange={event => setDraft(current => ({ ...current, title: event.target.value }))} placeholder="Например, запуск рабочих мест нового офиса" /></label>
          </div>
        </section>

        <section>
          <h3><span>2</span> Адрес и время</h3>
          <div className="visit-form-grid">
            <label className="wide"><span>Адрес *</span><input value={draft.address} onChange={event => setDraft(current => ({ ...current, address: event.target.value }))} placeholder="Город, улица, дом, офис" /></label>
            <label><span>Начало *</span><input type="datetime-local" value={draft.scheduledStart} onChange={event => setDraft(current => ({ ...current, scheduledStart: event.target.value }))} /></label>
            <label><span>Окончание окна</span><input type="datetime-local" value={draft.scheduledEnd} onChange={event => setDraft(current => ({ ...current, scheduledEnd: event.target.value }))} /></label>
          </div>
        </section>

        <section>
          <h3><span>3</span> Команда</h3>
          <div className="visit-assignee-picker">
            {assignees.map(person => {
              const name = person.full_name || person.fullName || 'Без имени';
              const checked = draft.assigneeUserIds.includes(person.id);
              return <label key={person.id} className={checked ? 'selected' : ''}><input type="checkbox" checked={checked} onChange={event => setDraft(current => {
                const ids = event.target.checked ? [...current.assigneeUserIds, person.id] : current.assigneeUserIds.filter(id => id !== person.id);
                return { ...current, assigneeUserIds: ids, leadUserId: ids.includes(current.leadUserId) ? current.leadUserId : (ids[0] || '') };
              })} /><span>{initials(name)}</span><p><b>{name}</b><small>{person.role || person.roles?.join(' · ') || 'Сотрудник'}</small></p><Check /></label>;
            })}
          </div>
          {draft.assigneeUserIds.length > 0 && <label className="visit-lead-select"><span>Ведущий специалист</span><select value={draft.leadUserId} onChange={event => setDraft(current => ({ ...current, leadUserId: event.target.value }))}>{draft.assigneeUserIds.map(id => { const person = assignees.find(item => item.id === id); return <option key={id} value={id}>{person?.full_name || person?.fullName || id}</option>; })}</select></label>}
        </section>

        <section>
          <div className="visit-form-section-head"><h3><span>4</span> Чек-лист</h3><button type="button" onClick={() => setDraft(current => ({ ...current, checklist: [...current.checklist, { title: '', isCompleted: false }] }))}><Plus /> Добавить пункт</button></div>
          <div className="visit-repeater">
            {draft.checklist.map((item, index) => <div key={item.id || index}><span>{index + 1}</span><input value={item.title} onChange={event => setDraft(current => ({ ...current, checklist: current.checklist.map((row, rowIndex) => rowIndex === index ? { ...row, title: event.target.value } : row) }))} placeholder="Что необходимо проверить или выполнить" /><button type="button" onClick={() => setDraft(current => ({ ...current, checklist: current.checklist.filter((_, rowIndex) => rowIndex !== index) }))}><X /></button></div>)}
            {!draft.checklist.length && <p>Чек-лист можно заполнить сейчас или оставить выезд без него.</p>}
          </div>
        </section>

        <section>
          <div className="visit-form-section-head"><h3><span>5</span> Материалы и расходы</h3><button type="button" onClick={() => setDraft(current => ({ ...current, materials: [...current.materials, { name: '', quantity: 1, unit: 'шт.', cost: 0 }] }))}><Plus /> Материал</button></div>
          <div className="visit-material-editor">
            {draft.materials.map((item, index) => <div key={index}><input value={item.name} onChange={event => setDraft(current => ({ ...current, materials: current.materials.map((row, rowIndex) => rowIndex === index ? { ...row, name: event.target.value } : row) }))} placeholder="Наименование" /><input type="number" min="0.001" step="0.001" value={item.quantity} onChange={event => setDraft(current => ({ ...current, materials: current.materials.map((row, rowIndex) => rowIndex === index ? { ...row, quantity: Number(event.target.value) } : row) }))} /><input value={item.unit} onChange={event => setDraft(current => ({ ...current, materials: current.materials.map((row, rowIndex) => rowIndex === index ? { ...row, unit: event.target.value } : row) }))} /><input type="number" min="0" step="0.01" value={item.cost} onChange={event => setDraft(current => ({ ...current, materials: current.materials.map((row, rowIndex) => rowIndex === index ? { ...row, cost: Number(event.target.value) } : row) }))} /><button type="button" onClick={() => setDraft(current => ({ ...current, materials: current.materials.filter((_, rowIndex) => rowIndex !== index) }))}><X /></button></div>)}
          </div>
          <div className="visit-form-grid visit-cost-grid"><label><span>Дополнительные расходы, ₽</span><input type="number" min="0" step="0.01" value={draft.expenses} onChange={event => setDraft(current => ({ ...current, expenses: Number(event.target.value) }))} /></label><label className="visit-switch"><input type="checkbox" checked={draft.isBillable} onChange={event => setDraft(current => ({ ...current, isBillable: event.target.checked }))} /><span /><p><b>Оплачиваемый выезд</b><small>Фактическое время попадёт в расчёт</small></p></label></div>
        </section>
        <footer><button type="button" className="visit-secondary-button" onClick={onClose}>Отмена</button><button className="visit-primary-button" disabled={mutation.isPending}>{mutation.isPending ? 'Сохраняем…' : mode === 'create' ? 'Запланировать выезд' : 'Сохранить изменения'} <ArrowRight /></button></footer>
      </form>
    </aside>
  </div>;
}

function CompletionDialog({ detail, value, onChange, pending, onClose, onSubmit }: { detail: VisitDetail; value: CompletionState; onChange: (value: CompletionState) => void; pending: boolean; onClose: () => void; onSubmit: () => void }) {
  const checklistReady = detail.checklist_completed === detail.checklist_total;
  return <div className="visit-dialog-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="visit-completion-dialog" role="dialog" aria-modal="true" aria-label="Завершение выезда">
      <header><span><CheckCircle2 /></span><div><small>Завершение выезда</small><h2>Зафиксировать результат</h2></div><button type="button" onClick={onClose}><X /></button></header>
      <div className={`visit-completion-check ${checklistReady ? 'ready' : 'blocked'}`}><ClipboardCheck /><p><b>Чек-лист: {detail.checklist_completed} из {detail.checklist_total}</b><small>{checklistReady ? 'Все обязательные пункты выполнены' : 'Завершите оставшиеся пункты'}</small></p>{checklistReady ? <Check /> : <AlertTriangle />}</div>
      <div className="visit-completion-times"><label><span>Время в пути, мин</span><input type="number" min="0" value={value.travelMinutes} onChange={event => onChange({ ...value, travelMinutes: Number(event.target.value) })} /></label><label><span>Работа на объекте, мин</span><input type="number" min="0" value={value.workMinutes} onChange={event => onChange({ ...value, workMinutes: Number(event.target.value) })} /></label></div>
      <label className="visit-completion-result"><span>Результат работ *</span><textarea rows={5} value={value.resultNote} onChange={event => onChange({ ...value, resultNote: event.target.value })} placeholder="Что выполнено, что проверено, какие рекомендации переданы клиенту" /></label>
      <label className="visit-completion-result"><span>Кто подтвердил результат</span><input value={value.confirmationName} onChange={event => onChange({ ...value, confirmationName: event.target.value })} placeholder="Имя представителя клиента" /></label>
      {detail.ticket_id && <label className="visit-worklog-option"><input type="checkbox" checked={value.createWorklog} onChange={event => onChange({ ...value, createWorklog: event.target.checked })} /><span><Check /></span><p><b>Создать трудозатрату в заявке</b><small>{detail.ticket_display_number} · категория «Выезд» · {value.workMinutes} мин</small></p></label>}
      <footer><button type="button" className="visit-secondary-button" onClick={onClose}>Вернуться</button><button type="button" className="visit-primary-button success" disabled={pending || !checklistReady || !value.resultNote.trim()} onClick={onSubmit}>{pending ? 'Завершаем…' : 'Завершить выезд'} <CheckCircle2 /></button></footer>
    </section>
  </div>;
}
